import React, { useMemo, useState } from 'react';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import PortalTooltip from './PortalTooltip';

import { MultiSelect } from 'components/ui/multi-select';

const parseNum = (str) => {
  if (!str) return 0;
  if (typeof str === 'number') return str;
  const num = parseFloat(String(str).replace(/,/g, '').replace(/\$|B|M|K|%/g, (match) => {
    switch (match) {
      case 'B': return 'e9';
      case 'M': return 'e6';
      case 'K': return 'e3';
      case '%': return '';
      default: return '';
    }
  }));
  return isNaN(num) ? 0 : num;
};

const ALL_METRICS = [
  { label: "Publications", key: "total_pubs", value: "total_pubs" },
  { label: "Citations", key: "total_citations", value: "total_citations" },
  { label: "H-Index", key: "h_index", value: "h_index" },
  { label: "Grant ($)", key: "total_grant_amount", value: "total_grant_amount" },
  { label: "Collabs", key: "int_collab", value: "int_collab" },
  { label: "Researchers", key: "active_researchers", value: "active_researchers" },
  { label: "Median Cites/Paper", key: "median_citations_per_paper", value: "median_citations_per_paper" },
  { label: "% Top 10% Cited", key: "pct_top10_cited", value: "pct_top10_cited" },
  { label: "G-Index", key: "g_index", value: "g_index" },
  { label: "Pubs/Faculty", key: "pubs_per_faculty", value: "pubs_per_faculty" }
];

const StrengthRadar = ({ entities, analysisRecords = [], isPeerMode }) => {
  const [hoveredEntityId, setHoveredEntityId] = useState(null);
  const [selectedMetricKeys, setSelectedMetricKeys] = useState([
    "total_pubs",
    "total_citations",
    "h_index",
    "total_grant_amount",
    "int_collab"
  ]);

  const radarData = useMemo(() => {
    const activeMetrics = ALL_METRICS.filter(m => selectedMetricKeys.includes(m.value));
    
    // Sort active metrics to match the order in selectedMetricKeys for consistent display
    activeMetrics.sort((a, b) => selectedMetricKeys.indexOf(a.value) - selectedMetricKeys.indexOf(b.value));

    return activeMetrics.map(m => {
      const point = { subject: m.label };
      
      // Find the maximum raw value for this metric across all selected entities
      let maxVal = 0;
      entities.forEach(entity => {
        const record = analysisRecords.find(r => r.id === entity.id);
        const val = parseNum(record?.[m.key]) || 0;
        if (val > maxVal) maxVal = val;
      });
      
      // Prevent division by zero
      if (maxVal === 0) maxVal = 1;

      entities.forEach(entity => {
        const record = analysisRecords.find(r => r.id === entity.id);
        const rawVal = parseNum(record?.[m.key]) || 0;
        // Normalize to 5-100 scale relative to the highest performer in the group
        point[entity.id] = Math.min(100, Math.max(5, Math.round((rawVal / maxVal) * 100)));
        // Store raw value for the tooltip
        point[`raw_${entity.id}`] = rawVal;
      });
      return point;
    });
  }, [analysisRecords, entities, selectedMetricKeys]);

  const CustomTooltip = ({ active, payload, label, coordinate }) => {
    if (active && payload && payload.length) {
      return (
        <PortalTooltip active={active} payload={payload} coordinate={coordinate}>
          <p className="font-bold text-slate-800 mb-2">{label}</p>
          {payload.map((entry, index) => {
            const entityId = entry.dataKey;
            const entity = entities.find(e => e.id == entityId);
            if (!entity) return null;
            const isHovered = hoveredEntityId == entityId;
            const isAnyHovered = hoveredEntityId !== null;
            const isBase = entity.isBaseEntity;
            
            let itemOpacity = 1;
            if (isPeerMode) {
              itemOpacity = (isBase || isHovered) ? 1 : 0.5;
            } else {
              itemOpacity = isAnyHovered ? (isHovered ? 1 : 0.5) : 1;
            }
            const shouldHighlight = isPeerMode ? (isBase || isHovered) : (isAnyHovered ? isHovered : false);
            
            const rawValue = payload[0]?.payload?.[`raw_${entityId}`] || 0;
            
            return (
              <div key={index} className="flex items-center justify-between gap-4 mb-1" style={{ opacity: itemOpacity }}>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: entry.color }} />
                  <span style={{ color: entry.color, fontWeight: shouldHighlight ? 'bold' : 'normal' }}>{entry.name}</span>
                </div>
                <span className="font-medium" style={{ color: entry.color, fontWeight: shouldHighlight ? 'bold' : 'normal' }}>
                  {rawValue.toLocaleString()}
                </span>
              </div>
            );
          })}
        </PortalTooltip>
      );
    }
    return null;
  };

  return (
    <div className="w-full h-full flex flex-col">
      <div className="flex flex-col items-center gap-3 mb-4 z-10">
        <div className="text-center w-full">
          <h3 className="text-lg font-semibold text-gray-800">Multi-Metric Comparison</h3>
          <p className="text-xs text-slate-500">Relative performance across key dimensions (0-100)</p>
        </div>
        <div className="w-full max-w-[300px]">
          <MultiSelect
            options={ALL_METRICS}
            value={selectedMetricKeys}
            onChange={(newVals) => {
              // Ensure at least 3 metrics are selected to form a polygon
              if (newVals.length >= 3) {
                setSelectedMetricKeys(newVals);
              }
            }}
            placeholder="Select metrics..."
            maxDisplay={2}
          />
        </div>
      </div>
      
      <div className="flex-1 w-full min-h-[250px] relative z-0">
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart cx="50%" cy="50%" outerRadius="75%" data={radarData} margin={{ top: 20, right: 30, bottom: 20, left: 30 }}>
            <PolarGrid stroke="#e2e8f0" />
            <PolarAngleAxis dataKey="subject" tick={{ fill: '#64748b', fontSize: 11 }} />
            <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fill: '#94a3b8', fontSize: 10 }} axisLine={false} />
            
            {entities.map(entity => {
              const isHovered = hoveredEntityId === entity.id;
              const isAnyHovered = hoveredEntityId !== null;
              
              let opacity = 0.35;
              let strokeOpacity = 1;
              let strokeW = 2.5;
              let strokeDash = "none";
              
              if (isPeerMode) {
                if (entity.isBaseEntity) {
                  opacity = 0.35;
                  strokeOpacity = 1;
                  strokeW = 3;
                  strokeDash = "none";
                } else {
                  opacity = isHovered ? 0.35 : 0.05;
                  strokeOpacity = isHovered ? 1 : 0.3;
                  strokeW = isHovered ? 2.5 : 1.5;
                  strokeDash = isHovered ? "none" : "4 4";
                }
              } else {
                opacity = 0.25;
                strokeOpacity = 0.8;
                strokeW = 2;
                strokeDash = "none";
              }
              
              return (
              <Radar 
                key={entity.id}
                name={entity.name} 
                dataKey={entity.id} 
                stroke={entity.color} 
                strokeOpacity={strokeOpacity}
                fill={entity.color} 
                fillOpacity={opacity} 
                strokeWidth={strokeW}
                strokeDasharray={strokeDash}
                onMouseEnter={() => setHoveredEntityId(entity.id)}
                onMouseLeave={() => setHoveredEntityId(null)}
              />
            )})}
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }} 
              onMouseEnter={(e) => setHoveredEntityId(e.dataKey)}
              onMouseLeave={() => setHoveredEntityId(null)}
            />
          </RadarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default StrengthRadar;
